import { isValidLngLat } from './isValidLngLat';

// Number of decimal places to round DMS conversions to.
const DECIMAL_PLACES = 6;

export type ParsedCoordinates = {
  lat: number;
  lng: number;
  format: 'DD' | 'DMS';
};

// Pattern for DMS format: degrees° minutes′ seconds″ direction
// Also handles alternative symbols: ' for minutes and " for seconds
const dmsPattern = /^(\d{1,3})\s*[°]\s*(\d{1,2})\s*[′']\s*(\d{1,2}(?:\.\d+)?)\s*[″"]\s*([NSEW])$/i;

// allows optional + or - sign, followed by digits, optional decimal point
const ddPattern = /^[+-]?([0-9]+([.][0-9]+)?|[.][0-9]+)$/;

const parseDecimal = (value: string): number | null => (ddPattern.test(value) ? parseFloat(value) : null);

const parseDms = (dmsString: string, isLatitude: boolean): number | null => {
  const match = dmsString.match(dmsPattern);
  if (!match) {
    return null;
  }

  const degrees = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = parseFloat(match[3]);
  const direction = match[4].toUpperCase();

  if (minutes >= 60 || seconds >= 60) {
    return null;
  }

  if (!(isLatitude ? ['N', 'S'] : ['E', 'W']).includes(direction)) {
    return null;
  }

  const decimalValue = Number((degrees + minutes / 60 + seconds / 3600).toFixed(DECIMAL_PLACES));

  return direction === 'S' || direction === 'W' ? -decimalValue : decimalValue;
};

/**
 * Parses a "lat, lng" string in Decimal Degrees (DD) or Degrees, Minutes, Seconds (DMS) format, optionally wrapped in
 * parentheses. Returns null unless both parts parse and are in range.
 */
export const parseCoordinates = (input: string): ParsedCoordinates | null => {
  if (!input || typeof input !== 'string') {
    return null;
  }

  const parts = input.replace(/[()]/g, '').trim().split(',');
  if (parts.length !== 2) {
    return null;
  }

  const latString = parts[0].trim();
  const lngString = parts[1].trim();

  const format = /[°′″'"]|[NSEW]$/i.test(latString + lngString) ? 'DMS' : 'DD';
  const lat = format === 'DMS' ? parseDms(latString, true) : parseDecimal(latString);
  const lng = format === 'DMS' ? parseDms(lngString, false) : parseDecimal(lngString);

  if (lat === null || lng === null || !isValidLngLat(lng, lat)) {
    return null;
  }

  return { lat, lng, format };
};
